import { CurrencyPipe } from '@angular/common';
import {
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
    inject
} from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { lastValueFrom } from 'rxjs';
import { PageableFilter, PageableRequest } from 'src/app/admin-api';
import { PageContent, TableColumn, TableComponent } from 'src/app/components/table';
import {
    BillingControlService,
    BillingControlSubscriberPaymentQueueItem,
    BillingControlSubscriberPaymentQueueTabSummary,
    BillingControlSubscriberPaymentStep,
    BillingControlSubscriberPaymentStepMessageSummary
} from 'src/app/services/billing-control.service';
import { LoaderService } from 'src/app/services/loader.service';

export type ActivationFilter = 'all' | 'waiting' | 'no_token' | 'edition_ok';

@Component({
  selector: 'app-billing-control-subscriber-steps',
  templateUrl: './billing-control-subscriber-steps.component.html',
  styleUrls: ['./billing-control-subscriber-steps.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [CurrencyPipe]
})
export class BillingControlSubscriberStepsComponent implements OnInit, OnChanges {
  @Input({ required: true }) step!: number;
  @Input({ required: true }) description!: string;
  @Input() titleLabel = '';
  @Input() cronJobName?: string;
  @Input() paymentRoundId: number | null = null;
  @Input() initialMessageFilter: string | null = null;
  @Input() showActivationFilters = false;

  @ViewChild(TableComponent) table?: TableComponent;

  billingControlService = inject(BillingControlService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private currencyPipe = inject(CurrencyPipe);

  queueTabSummary?: BillingControlSubscriberPaymentQueueTabSummary;
  messageSummaries: BillingControlSubscriberPaymentStepMessageSummary[] = [];
  isSummariesLoading = false;
  isActionLoading = false;
  stepCols: TableColumn[] = [];
  activationFilter: ActivationFilter = 'all';
  messageContains: string | null = null;
  waitingOnly = false;

  readonly activationFilterOptions = [
    { label: 'Todos', value: 'all' as ActivationFilter },
    { label: 'Aguardando ativação', value: 'waiting' as ActivationFilter },
    { label: 'Sem token', value: 'no_token' as ActivationFilter },
    { label: 'Edição já existia (ok)', value: 'edition_ok' as ActivationFilter }
  ];

  async ngOnInit(): Promise<void> {
    this.applyInitialMessageFilter();
    this.stepCols = this.step === 1 ? this.buildQueueCols() : this.buildStepCols();
    await this.loadSummaries();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['initialMessageFilter'] && !changes['initialMessageFilter'].firstChange) {
      this.applyInitialMessageFilter();
      this.reloadTable();
    }
    if (changes['paymentRoundId'] && !changes['paymentRoundId'].firstChange) {
      await this.loadSummaries();
      this.reloadTable();
    }
  }

  onActivationFilterChange(): void {
    this.messageContains = null;
    this.waitingOnly = false;
    if (this.activationFilter === 'no_token') {
      this.messageContains = 'token';
    } else if (this.activationFilter === 'edition_ok') {
      this.messageContains = 'confirmada para esta assinatura';
    } else if (this.activationFilter === 'waiting') {
      this.waitingOnly = true;
    }
    this.reloadTable();
  }

  private applyInitialMessageFilter(): void {
    if (!this.initialMessageFilter) {
      this.messageContains = null;
      this.waitingOnly = false;
      this.activationFilter = 'all';
      return;
    }
    this.messageContains = this.initialMessageFilter;
    this.waitingOnly = false;
    if (this.showActivationFilters && this.isNoTokenFilter(this.initialMessageFilter)) {
      this.activationFilter = 'no_token';
    }
  }

  private isNoTokenFilter(value: string): boolean {
    return value.toLowerCase().includes('token');
  }

  private reloadTable(): void {
    void this.table?.pageTable();
  }

  private buildStepCols(): TableColumn[] {
    return [
      new TableColumn.Builder()
        .setHeader('Assinante')
        .setField('subscriberId')
        .setType('number')
        .setRouterLink('/users/subscribers/')
        .setRouterLinkFieldName('subscriberId')
        .setCondition('equals')
        .build(),
      new TableColumn.Builder()
        .setHeader('Rodada')
        .setField('paymentRoundId')
        .setType('number')
        .setCondition('equals')
        .build(),
      new TableColumn.Builder()
        .setHeader('Etapa')
        .setField('step')
        .setType('number')
        .setCondition('equals')
        .setDisplayFunction((item: BillingControlSubscriberPaymentStep) =>
          this.formatStepLabel(item.step)
        )
        .build(),
      new TableColumn.Builder()
        .setHeader('Situação')
        .setField('lastMessage')
        .setType('text')
        .setCondition('contains')
        .setDisplayFunction((item: BillingControlSubscriberPaymentStep) =>
          this.formatSituationMessage(item.lastMessage)
        )
        .build(),
      new TableColumn.Builder()
        .setHeader('Pagamento')
        .setField('creditCardPaymentId')
        .setType('number')
        .setCondition('equals')
        .build(),
      new TableColumn.Builder()
        .setHeader('Atualizado em')
        .setField('dateUpdated')
        .setType('date')
        .setCondition('between')
        .build()
    ];
  }

  private buildQueueCols(): TableColumn[] {
    return [
      new TableColumn.Builder()
        .setHeader('Assinante')
        .setField('subscriberId')
        .setType('number')
        .setRouterLink('/users/subscribers/')
        .setRouterLinkFieldName('subscriberId')
        .setCondition('equals')
        .build(),
      new TableColumn.Builder()
        .setHeader('Rodada')
        .setField('paymentRoundId')
        .setType('number')
        .setCondition('equals')
        .build(),
      new TableColumn.Builder()
        .setHeader('Nome')
        .setField('firstName')
        .setCondition('contains')
        .setDisplayFunction((item: BillingControlSubscriberPaymentQueueItem) =>
          this.formatPersonName(item)
        )
        .build(),
      new TableColumn.Builder()
        .setHeader('E-mail')
        .setField('email')
        .setCondition('contains')
        .build(),
      new TableColumn.Builder()
        .setHeader('Gateway')
        .setField('gatewayName')
        .setCondition('contains')
        .setDisplayFunction((item: BillingControlSubscriberPaymentQueueItem) =>
          this.formatGatewayLabel(item)
        )
        .build(),
      new TableColumn.Builder()
        .setHeader('Plano')
        .setField('subscriptionTypeName')
        .setCondition('contains')
        .build(),
      new TableColumn.Builder()
        .setHeader('Valor')
        .setField('amount')
        .setType('currency')
        .setCondition('equals')
        .build(),
      new TableColumn.Builder()
        .setHeader('Situação')
        .setField('lastMessage')
        .setType('text')
        .setCondition('contains')
        .setDisplayFunction((item: BillingControlSubscriberPaymentQueueItem) => {
          if (item.creditCardPaymentId && !item.lastMessage) {
            return 'Já tentou — aguardando retorno';
          }
          if (!item.creditCardPaymentId && !item.lastMessage) {
            return 'Na fila';
          }
          return this.formatSituationMessage(item.lastMessage);
        })
        .build(),
      new TableColumn.Builder()
        .setHeader('Atualizado em')
        .setField('dateUpdated')
        .setType('date')
        .setCondition('between')
        .build()
    ];
  }

  private async loadSummaries(): Promise<void> {
    // Sem rodada, o GROUP BY em todo o histórico estoura timeout e dispara
    // um aviso por aba (5x). Totalizadores exigem filtro de rodada.
    if (this.paymentRoundId == null) {
      this.queueTabSummary = undefined;
      this.messageSummaries = [];
      this.isSummariesLoading = false;
      return;
    }

    this.isSummariesLoading = true;
    try {
      if (this.step === 1) {
        this.queueTabSummary = await lastValueFrom(
          this.billingControlService.summarizeSubscriberPaymentQueue(
            this.step,
            this.paymentRoundId
          )
        );
        this.messageSummaries = this.queueTabSummary?.byLastMessage ?? [];
        return;
      }

      this.messageSummaries = await lastValueFrom(
        this.billingControlService.summarizeSubscriberPaymentStepsByLastMessage(
          this.step,
          this.paymentRoundId
        )
      );
      this.queueTabSummary = undefined;
    } catch (error) {
      this.queueTabSummary = undefined;
      this.messageSummaries = [];
      console.error(
        `Falha ao carregar totalizadores step=${this.step} round=${this.paymentRoundId}`,
        error
      );
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: `Não foi possível carregar os totalizadores da etapa ${this.step} (rodada #${this.paymentRoundId}).`,
        life: 4000
      });
    } finally {
      this.isSummariesLoading = false;
    }
  }

  /** Evita dump de webhook/PII no card; mantém mensagem humana curta. */
  formatSituationMessage(raw: string | null | undefined): string {
    const text = raw != null ? String(raw).trim() : '';
    // Sucesso no pipeline limpa LastMessage; no FINISHED isso é "Concluído",
    // nas demais etapas ainda é espera (sem erro gravado).
    if (!text || text === '(sem mensagem)') {
      return this.step === 4 ? 'Concluído' : 'Aguardando';
    }
    if (
      text.includes('PagarmeWebhookResponse') ||
      text.includes('payload ') ||
      text.toLowerCase().includes('pagamento não encontrado') ||
      text.toLowerCase().includes('pagamento nao encontrado') ||
      text.length > 180
    ) {
      const acquirer = text.match(/acquirer_message=([^,)\]]+)/i);
      if (acquirer?.[1]) {
        return acquirer[1].trim();
      }
      if (
        text.toLowerCase().includes('pagament') &&
        (text.toLowerCase().includes('não encontrado') ||
          text.toLowerCase().includes('nao encontrado'))
      ) {
        return 'Webhook: pagamento não encontrado no gateway';
      }
      return 'Mensagem técnica do gateway (detalhe no registro)';
    }
    return text;
  }

  /** Última tentativa; se a fila aponta outro gateway, mostra os dois. */
  formatGatewayLabel(item: BillingControlSubscriberPaymentQueueItem): string {
    const last = item.gatewayName?.trim() || 'Sem gateway';
    const queue = item.queueGatewayName?.trim();
    if (
      item.creditCardPaymentId &&
      queue &&
      item.queueGatewayId != null &&
      item.gatewayId != null &&
      item.queueGatewayId !== item.gatewayId
    ) {
      return `${last} (última) · próximo: ${queue}`;
    }
    return last;
  }

  findPage = async (
    request: PageableRequest
  ): Promise<PageContent<BillingControlSubscriberPaymentStep | BillingControlSubscriberPaymentQueueItem>> => {
    const filters: PageableFilter[] = [...(request.filters ?? [])];

    if (this.paymentRoundId != null) {
      filters.push({
        field: 'paymentRoundId',
        value: String(this.paymentRoundId),
        condition: 'equals',
        fieldType: 'number'
      });
    }

    if (this.messageContains) {
      filters.push({
        field: 'lastMessage',
        value: this.messageContains,
        condition: 'contains',
        fieldType: 'text'
      });
    }

    if (this.waitingOnly) {
      // PageableCustomRepository exige value != null mesmo em isNull.
      filters.push({
        field: 'lastMessage',
        value: '1',
        condition: 'isNull',
        fieldType: 'text'
      });
    }

    const payload: PageableRequest = {
      ...request,
      filters
    };

    if (this.step === 1) {
      return lastValueFrom(
        this.billingControlService.listSubscriberPaymentQueue(this.step, payload)
      );
    }

    return lastValueFrom(
      this.billingControlService.listSubscriberPaymentSteps(this.step, payload)
    );
  };

  confirmTriggerCronJob(event: Event): void {
    if (!this.cronJobName) {
      return;
    }

    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Deseja processar agora quem está parado na etapa “${this.titleLabel || this.step}”?`,
      icon: 'pi pi-refresh',
      acceptLabel: 'Processar',
      rejectLabel: 'Cancelar',
      accept: () => void this.triggerCronJob()
    });
  }

  private async triggerCronJob(): Promise<void> {
    if (!this.cronJobName || this.isActionLoading) {
      return;
    }

    this.isActionLoading = true;
    LoaderService.showLoader();
    try {
      await lastValueFrom(
        this.billingControlService.triggerCronJob(this.cronJobName)
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Processamento disparado',
        detail: `A etapa “${this.titleLabel || this.step}” foi enfileirada para processar agora.`
      });
      await this.loadSummaries();
      this.reloadTable();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível processar esta etapa agora.'
      });
    } finally {
      this.isActionLoading = false;
      LoaderService.showLoader(false);
    }
  }

  formatCurrency(value?: number): string {
    return this.currencyPipe.transform(value ?? 0, 'BRL', 'symbol', '1.2-2') ?? 'R$ 0,00';
  }

  private formatPersonName(item: BillingControlSubscriberPaymentQueueItem): string {
    const parts = [item.firstName, item.lastName].filter((part) => !!part);
    return parts.length > 0 ? parts.join(' ') : '-';
  }

  private formatStepLabel(step?: number): string {
    switch (step) {
      case 0:
        return 'Validação';
      case 1:
        return 'Cobrança';
      case 2:
        return 'Ativação';
      case 3:
        return 'Edição';
      case 4:
        return 'Finalizado';
      default:
        return step != null ? String(step) : '-';
    }
  }
}
